import { useState, useEffect, useCallback } from "react";
import { getFeatureRequests, getBugReports } from "../data/store";
import {
  getPipelineState,
  approveStage,
  rejectStage,
  enableGate,
  disableGate,
  triggerPipeline,
  stopPipeline,
  retryStage,
  type PipelineStageStatus,
} from "../services/pipeline";
import AwsConfig from "../components/AwsConfig";

export default function Dashboard() {
  const [stages, setStages] = useState<PipelineStageStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const features = getFeatureRequests();
  const bugs = getBugReports();
  const openFeatures = features.filter((f) => f.status === "open" || f.status === "in-progress").length;
  const openBugs = bugs.filter((b) => b.status === "open" || b.status === "in-progress").length;

  const refresh = useCallback(async () => {
    if (!connected) return;
    setLoading(true);
    setError(null);
    try {
      setStages(await getPipelineState());
    } catch (err: any) {
      setError(err.message || "Failed to fetch pipeline");
    } finally {
      setLoading(false);
    }
  }, [connected]);

  useEffect(() => {
    if (connected) {
      refresh();
      const id = setInterval(refresh, 20000);
      return () => clearInterval(id);
    }
  }, [connected, refresh]);

  async function action(key: string, fn: () => Promise<any>) {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  }

  // Determine current execution ID from any active stage
  const currentExecutionId = stages.find((s) => s.latestExecution)?.latestExecution?.pipelineExecutionId || "";

  function getStageType(name: string): "source" | "build" | "deploy" | "approval" {
    if (name.includes("Source")) return "source";
    if (name.includes("Build")) return "build";
    if (name.includes("Approv")) return "approval";
    return "deploy";
  }

  function getStageEnv(name: string): string {
    if (name.includes("Dev")) return "dev";
    if (name.includes("Beta")) return "beta";
    if (name.includes("Prod")) return "prod";
    return "";
  }

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2>POS System Pipeline</h2>
            <p>Control deployments — open gates, approve releases, rollback.</p>
          </div>
          <AwsConfig onConfigured={() => setConnected(true)} />
        </div>
      </div>

      {error && (
        <div className="error-banner">
          ⚠️ {error}
          <button onClick={() => setError(null)} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", fontSize: "1rem" }}>✕</button>
        </div>
      )}

      {connected ? (
        <div className="pipeline-card">
          {/* Top controls */}
          <div className="pc-header">
            <div className="pc-title">
              <span className="pc-name">pos-system-deploy</span>
              <span className={`pc-status-pill ${stages.some((s) => s.status === "InProgress") ? "running" : stages.some((s) => s.status === "Failed") ? "failed" : "idle"}`}>
                {stages.some((s) => s.status === "InProgress") ? "Running" : stages.some((s) => s.status === "Failed") ? "Failed" : "Idle"}
              </span>
            </div>
            <div className="pc-controls">
              <button className="ctrl-btn trigger" onClick={() => action("trigger", triggerPipeline)} disabled={busy === "trigger"}>
                {busy === "trigger" ? "⟳" : "▶"} New Release
              </button>
              {currentExecutionId && stages.some((s) => s.status === "InProgress") && (
                <button className="ctrl-btn stop" onClick={() => action("stop", () => stopPipeline(currentExecutionId, "Rollback via DevPortal"))} disabled={busy === "stop"}>
                  {busy === "stop" ? "⟳" : "⏹"} Stop & Rollback
                </button>
              )}
              <button className="ctrl-btn refresh" onClick={refresh} disabled={loading}>
                {loading ? "⟳" : "↻"}
              </button>
            </div>
          </div>

          {/* Stage rows */}
          <div className="pc-stages">
            {stages.map((stage) => {
              const type = getStageType(stage.stageName);
              const env = getStageEnv(stage.stageName);
              const approvalAction = stage.actionStates.find((a) => a.latestExecution?.token);
              const gateEnabled = stage.inboundTransitionState?.enabled !== false;
              const isFailed = stage.status === "Failed";
              const isActive = stage.status === "InProgress";

              return (
                <div key={stage.stageName} className={`pc-stage ${isActive ? "active" : ""} ${isFailed ? "failed" : ""} ${!gateEnabled ? "gated" : ""}`}>
                  {/* Gate indicator */}
                  <div className="stage-gate">
                    {stage.inboundTransitionState !== undefined && (
                      <button
                        className={`gate-toggle ${gateEnabled ? "open" : "closed"}`}
                        onClick={() => action(`gate-${stage.stageName}`, () => gateEnabled ? disableGate(stage.stageName) : enableGate(stage.stageName))}
                        disabled={busy === `gate-${stage.stageName}`}
                        title={gateEnabled ? "Click to disable transition (close gate)" : "Click to enable transition (open gate)"}
                      >
                        {gateEnabled ? "🟢" : "🔴"}
                      </button>
                    )}
                  </div>

                  {/* Stage info */}
                  <div className="stage-body">
                    <div className="stage-row-header">
                      <span className="stage-row-name">{stage.stageName.replace(/_/g, " ")}</span>
                      <span className={`stage-row-status s-${stage.status.toLowerCase()}`}>{stage.status}</span>
                    </div>

                    {/* Actions for this stage */}
                    <div className="stage-row-actions">
                      {/* Approval controls */}
                      {type === "approval" && approvalAction?.latestExecution?.token && (
                        <>
                          <button
                            className="action-btn approve"
                            onClick={() => action(`approve-${stage.stageName}`, () => approveStage(stage.stageName, approvalAction.actionName, approvalAction.latestExecution!.token!))}
                            disabled={busy === `approve-${stage.stageName}`}
                          >
                            ✓ Approve
                          </button>
                          <button
                            className="action-btn reject"
                            onClick={() => action(`reject-${stage.stageName}`, () => rejectStage(stage.stageName, approvalAction.actionName, approvalAction.latestExecution!.token!))}
                            disabled={busy === `reject-${stage.stageName}`}
                          >
                            ✕ Reject
                          </button>
                        </>
                      )}

                      {/* Retry failed */}
                      {isFailed && stage.latestExecution && (
                        <button
                          className="action-btn retry"
                          onClick={() => action(`retry-${stage.stageName}`, () => retryStage(stage.stageName, stage.latestExecution!.pipelineExecutionId))}
                          disabled={busy === `retry-${stage.stageName}`}
                        >
                          ↻ Retry
                        </button>
                      )}

                      {/* Gate controls */}
                      {!gateEnabled && stage.inboundTransitionState?.disabledReason && (
                        <span className="gate-reason">{stage.inboundTransitionState.disabledReason}</span>
                      )}
                    </div>
                  </div>

                  {/* Environment badge */}
                  {env && <span className={`env-badge env-${env}`}>{env}</span>}
                </div>
              );
            })}
          </div>

          {stages.length === 0 && !loading && (
            <div className="pc-empty">Click Refresh or connect to see pipeline stages.</div>
          )}
        </div>
      ) : (
        <div className="pipeline-disconnected">
          <div className="disconnect-icon">🔌</div>
          <h3>Connect to AWS</h3>
          <p>Enter Tooling account credentials to control the POS System pipeline.</p>
        </div>
      )}

      {/* Quick stats */}
      <div className="card-grid" style={{ marginTop: "1.5rem" }}>
        <div className="stat-card">
          <div className="label">Open Features</div>
          <div className="value" style={{ color: openFeatures > 0 ? "var(--primary)" : "inherit" }}>{openFeatures}</div>
        </div>
        <div className="stat-card">
          <div className="label">Open Bugs</div>
          <div className="value" style={{ color: openBugs > 0 ? "var(--danger)" : "inherit" }}>{openBugs}</div>
        </div>
      </div>
    </div>
  );
}
